import { IpaType, primaryStress, secondaryStress } from "../phonemes/ipa";

export interface PhonotacticsInput {
  /** map from number of syllables in a word to a weight of how likely that number is */
  readonly syllableCounts?: Record<number, number>;
  /** syllable to stress, counting from end if <0, none if 0 */
  readonly stressSyllable: number | 'spanish';
  /** syllable for secondary stress, counting from end if <0, none if 0 */
  readonly secondaryStress?: number;

  /** onset (in IPA), either list in priority order or mapped to a weight of how likely it is */
  readonly firstOnsets: IpaType[] | Record<IpaType, number>;
  readonly otherOnsets: IpaType[] | Record<IpaType, number>;
  /** nucleus (in IPA), either list in priority order or mapped to a weight of how likely it is */
  readonly nucleus: IpaType[] | Record<IpaType, number>;
  /** coda (in IPA), either list in priority order or mapped to a weight of how likely it is */
  readonly lastCodas: IpaType[] | Record<IpaType, number>;
  readonly otherCodas: IpaType[] | Record<IpaType, number>;

  /** phoneme category abbreviations (e.g., V or C) mapped to segments */
  readonly catToSegment?: Record<string, IpaType[]>;
}

export function getEmptyPhonotacticsInput(): PhonotacticsInput {
  return { stressSyllable: 0, secondaryStress: 0, firstOnsets: [], otherOnsets: [], nucleus: [], lastCodas: [], otherCodas: [] };
}

/** generate random words from phonotactics rules */
export class WordGenerator {
  private readonly makeCount?: CountGenerator;
  private readonly makeFirstOnset: ItemGenerator;
  private readonly makeOtherOnset: ItemGenerator;
  private readonly makeNucleus: ItemGenerator;
  private readonly makeLastCoda: ItemGenerator;
  private readonly makeOtherCoda: ItemGenerator;

  constructor(private readonly input: PhonotacticsInput) {
    this.makeCount = input.syllableCounts ? new CountGenerator(input.syllableCounts) : undefined;
    this.makeFirstOnset = new ItemGenerator(input.firstOnsets, input.catToSegment);
    this.makeOtherOnset = new ItemGenerator(input.otherOnsets, input.catToSegment);
    this.makeNucleus = new ItemGenerator(input.nucleus, input.catToSegment);
    this.makeLastCoda = new ItemGenerator(input.lastCodas, input.catToSegment);
    this.makeOtherCoda = new ItemGenerator(input.otherCodas, input.catToSegment);
  }

  makeWord(count?: number): IpaType {
    if (!count) {
      count = this.makeCount ? this.makeCount.generate() : 2;
    }

    const syllables = [];
    for (let i = 0; i < count; i++) {
      syllables.push(this.makeSyllable(i === 0, i === count - 1));
    }

    // add stress symbols
    const n1 = getStressSyllable(syllables, this.input.stressSyllable);
    const withPrimaryStress = addStress(syllables, n1, primaryStress);
    const n2 = this.input.secondaryStress === undefined ? 0 : getSecondaryStress(withPrimaryStress, this.input.secondaryStress);
    const withSecondaryStress = n1 === n2 ? withPrimaryStress : addStress(withPrimaryStress, n2, secondaryStress);
    return withSecondaryStress.join('');
  }

  makeSyllable(first: boolean, last: boolean): IpaType {
    const onset = first ? this.makeFirstOnset.generate() : this.makeOtherOnset.generate();
    const nucleus = this.makeNucleus.generate();
    const coda = last ? this.makeLastCoda.generate() : this.makeOtherCoda.generate();
    return onset + nucleus + coda;
  }
}

/** figure out which syllable to stress based on either the syllable # or special Spanish rules */
function getStressSyllable(syllables: IpaType[], stressSyllable: number | 'spanish'): number {
  const lastSyllable = syllables[syllables.length - 1];
  const lastPhoneme = lastSyllable[lastSyllable.length - 1];
  // either explicit # or assuming Spanish rules
  const which = typeof stressSyllable === 'number' ? stressSyllable
    : ['a', 'e', 'i', 'o', 'u', 'n', 's'].includes(lastPhoneme) ? -2 : -1;
  if (which === 0) {
    return 0;  // rule says not to stress this word
  }
  let n = which > 0 ? which : syllables.length + which + 1;
  n = Math.min(n, syllables.length);
  return Math.max(n, 1);
}

/** figure out where secondary stress should go, defaulting to none if too few syllables */
function getSecondaryStress(syllables: IpaType[], stressSyllable: number): number {
  let n = stressSyllable >= 0 ? stressSyllable : syllables.length + stressSyllable + 1;
  if (n <= 0 || n > syllables.length) {
    return 0;  // don't stress
  }
  return n;
}

/** add stress to the start of the nth syllable */
function addStress(syllables: IpaType[], n: number, stressIpa: IpaType): IpaType[] {
  const newSyllables: IpaType[] = [];
  let i = 0;
  for (const oldSyllable of syllables) {
    let newSyllable = (i + 1 === n) ? stressIpa : '';
    newSyllable += oldSyllable;
    newSyllables.push(newSyllable);
    i++;
  }
  return newSyllables;
}

/** randomly return on of the items from a list, based on the priority or weights */
class ItemGenerator {
  private readonly options: string[];
  private readonly totalWeight: number;
  private readonly weights: number[];

  constructor(
    weights: string[] | Record<string, number>,
    private readonly catToSegment?: Record<string, IpaType[]>
  ) {
    if (Array.isArray(weights)) {
      this.options = weights.map(v => v.toString());
      // weight earlier ones more than later ones
      const len = this.options.length;
      let last = 0;
      this.weights = this.options.map((_, i) => { last += len - i; return last; });
      this.totalWeight = this.weights[this.weights.length - 1];
    } else {
      this.options = Object.keys(weights);
      this.totalWeight = this.options.reduce((p: number, curr: string) => p + weights[curr], 0);
      let last = 0;
      this.weights = this.options.map((k, i) => { last = weights[k] + last; return last; });
    }
  }

  generate(): string {
    if (this.totalWeight === undefined) {
      return '';
    }
    const rand = Math.random() * this.totalWeight;
    const i = this.weights.findIndex(w => w > rand);
    const result = this.options[i];
    return this.interpretResult(result);
  }

  /**
   * if `result` contains categories, randomly pick segments from them,
   * else return result
   */
  private interpretResult(result: string): string {
    if (result === '∅') {
      return '';
    } else if (this.catToSegment === undefined) {
      return result;
    }

    let output = '';
    for (let i = 0; i < result.length; i++) {
      const c = result[i];
      const cat = this.catToSegment[c];
      if (cat !== undefined) {  // randomly pick segment in category
        const rand = Math.floor(Math.random() * cat.length);
        output += cat[rand];
      } else {  // use char as-is
        output += c;
      }
    }
    return output;
  }
}

class CountGenerator {
  private readonly options: number[];
  private readonly totalWeight: number;
  private readonly weights: number[];

  constructor(weights: Record<number, number>) {
    this.options = Object.keys(weights).map(k => Number.parseInt(k));
    this.totalWeight = this.options.reduce((p, curr) => p + weights[curr], 0);
    let last = 0;
    this.weights = this.options.map(k => { last = weights[k] + last; return last; });
  }

  generate(): number {
    const rand = Math.random() * this.totalWeight;
    const i = this.weights.findIndex(w => w > rand);
    return this.options[i];
  }
}
